"use client";
import { MEDIA_ASSETS } from "@/lib/media-assets";

/**
 * Route-aware heavy-asset preloader.
 *
 * Why this exists
 * ───────────────
 * The home page renders a large remote 3D scene (the Spline galaxy, a
 * ~23 MB `scene.splinecode`). If the page is revealed before that file is
 * downloaded the hero shows only the flat "default" backdrop for many
 * seconds, then pops in. The user wants the heavy file fully downloaded
 * BEHIND the loading overlay, and entry granted only once it's in — and
 * the same behaviour applied when navigating to a heavy page.
 *
 * This module is the single source of truth for:
 *   • which remote assets a route must fully download before reveal,
 *   • which routes mount a cinematic scene that announces `refgd:scene-ready`,
 *   • a streaming downloader that reports real byte progress, warms the HTTP
 *     cache, and persists the scene into the Cache Storage API so it is
 *     downloaded ONCE and served locally on every future visit (paired with
 *     the scene service worker in `public/sw.js`, which serves Spline's own
 *     fetch of the same file from that cache).
 */

// Served same-origin (and brotli-compressed) by app/hero-scene.splinecode/
// route.ts, which sets `Cache-Control: immutable` so the browser's own disk
// cache reuses it on every repeat visit. (Legacy absolute CDN URLs are still
// handled by the Cache Storage path below; same-origin relies on the HTTP
// cache instead, which sidesteps the service-worker double-decode hazard a
// Content-Encoding response would otherwise hit.)
export const HOME_SCENE_URL = "/hero-scene.splinecode";

/**
 * Cache Storage bucket the heavy scene is persisted into so it downloads ONCE
 * and is reused on every future visit. Must stay in sync with CACHE in
 * `public/sw.js` — the service worker serves Spline's own fetch from the same
 * bucket, while the preloader (below) seeds it from its progress download.
 */
const SCENE_CACHE = "refgd-scene-v1";

/**
 * Durable Cache Storage bucket for the captured hero video bytes.
 *
 * The browser HTTP cache is NOT a reliable home for the ~32 MB hero clip: a
 * <video> element fetches it with byte-RANGE requests, and reusing a
 * fetch()-warmed full-GET entry for those ranges does not hold on desktop or
 * iOS — so on every hard refresh the clip re-streams from the origin and the
 * splash bar crawls from 0. The in-memory object URL we publish per session
 * fixes playback WITHIN a load but is destroyed on reload.
 *
 * So we additionally persist the exact full file here (Cache Storage has a
 * large, durable quota) and rebuild the object URL straight from it on every
 * load — making the clip download exactly once, ever. Bump the version suffix
 * to force a one-time refetch for all visitors.
 */
const MEDIA_CACHE = "refgd-media-v1";

/** URLs whose freshness we've already HEAD-checked during this page load. */
const mediaRevalidated = new Set<string>();

/** True for the heavy remote Spline scene file (the only thing we persist). */
function isSceneAsset(url: string): boolean {
  return /\.splinecode(\?|$)/.test(url);
}

export type HeavyAsset = { url: string; bytesHint?: number; captureBlob?: boolean };

/** Normalise a pathname: strip trailing slashes, default to "/". */
function norm(pathname: string): string {
  if (!pathname) return "/";
  const p = pathname.replace(/\/+$/, "");
  return p === "" ? "/" : p;
}

/**
 * The heavy Spline galaxy now mounts on mobile too (it renders a static,
 * idle-frozen hero there). So the only case where the scene is NOT shown —
 * and therefore where we must NOT download 23 MB nor wait on a scene-ready
 * event that will never fire — is prefers-reduced-motion, which falls back to
 * the lightweight MobileStars canvas.
 */
export function sceneEligible(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  } catch {
    /* matchMedia unavailable — assume eligible */
  }
  return true;
}

/**
 * Heavy CROSS-NETWORK assets a route must fully download before its content
 * is revealed. Local same-origin assets (e.g. the evade frame sequence) are
 * handled by their own component plus the scene-ready signal and don't need
 * listing here.
 */
export function heavyAssetsForPath(pathname: string): HeavyAsset[] {
  const p = norm(pathname);
  // Evade-Cancelations hero plays a ~13 MB neon-vortex MP4. Download it IN FULL
  // behind the loading overlay so the CDN-backed <video> has no buffering when
  // the page is revealed. This gates the splash on the whole file, which is the
  // desired "fully load before reveal" behaviour for this route.
  if (p === "/evade-cancelations") {
    return [{
      url: MEDIA_ASSETS.evadeHeroVortex.url,
      bytesHint: MEDIA_ASSETS.evadeHeroVortex.bytes,
    }];
  }
  // Exclusive-Mentorships hero plays an ~8 MB "Liquid Reflections" boomerang
  // loop. Download it IN FULL behind the loading overlay so the <video> plays
  // straight from the CDN once the page is revealed — no buffering, no pop-in.
  // The resumable downloader below keeps this going across mobile tab
  // suspensions, and the video component announces `refgd:scene-ready` once it
  // can play so the splash lifts promptly.
  if (p === "/exclusive-mentorships") {
    return [{
      url: MEDIA_ASSETS.mentorshipBackground.url,
      bytesHint: MEDIA_ASSETS.mentorshipBackground.bytes,
    }];
  }
  // The home page is the usual first visit, so we DO gate it: download the
  // ~32 MB "sphere montage" hero from the CDN in full behind the splash so the
  // hero plays with zero buffering on reveal.
  // CosmicJourney dispatches `refgd:scene-ready` on the video's loadeddata, so
  // the splash lifts the instant the download finishes + the first frame is
  // ready (no wasted grace). On a slow first visit this is bounded by the 30 s
  // ceiling in LoadingScreen; repeat visits are instant from the immutable
  // cache. (The old files left in public/ are stale, unused rollback assets.)
  if (p === "/") {
    // captureBlob: keep the exact bytes we stream here and republish them as an
    // in-memory object URL (see downloadAsset / publishHeroBlobUrl). The hero
    // <video> then plays 100% from memory instead of depending on the browser
    // reusing this fetch's HTTP cache for its byte-range requests — which was
    // NOT holding on desktop or iOS, so the clip re-streamed from the
    // non-edge-cached origin and buffered even though the splash had "already
    // downloaded" it.
    return [{
      url: MEDIA_ASSETS.sphereMontage.url,
      bytesHint: MEDIA_ASSETS.sphereMontage.bytes,
      captureBlob: true,
    }];
  }
  return [];
}

/**
 * Routes that mount a cinematic scene which dispatches `refgd:scene-ready`
 * once its first frame has actually painted. The loader waits for that
 * signal (in addition to any heavy downloads) so the overlay never lifts
 * onto a not-yet-rendered canvas.
 */
export function pathHasScene(pathname: string): boolean {
  const p = norm(pathname);
  // Home no longer mounts a WebGL scene that announces `refgd:scene-ready`; its
  // hero is a canvas frame sequence that paints immediately, so the loader must
  // NOT wait on a scene-ready signal here (it would never resolve the long way).
  if (p === "/evade-cancelations") return true;
  return false;
}

/**
 * Routes that OPT OUT of the boot splash entirely — no cinematic overlay, no
 * body-scroll lock, even on a cold direct load. The community hub is an
 * app-like, instantly-available section (owner: "no loading screen for this
 * section"), so it must never sit behind the cosmic splash the way the
 * marketing pages do.
 */
export function splashDisabledForPath(pathname: string): boolean {
  const p = norm(pathname);
  return p === "/community" || p.startsWith("/community/");
}

/**
 * Resolve once the page is foregrounded (or immediately if it already is).
 * Used to pause-and-resume heavy downloads around mobile tab suspensions:
 * while the tab is hidden the browser runs no timers and may have killed the
 * in-flight fetch, so we hold here until the user returns and then continue
 * the stream. Also resolves if the supplied abort signal fires so callers can
 * bail out instead of waiting forever.
 */
function waitUntilVisible(signal?: AbortSignal): Promise<void> {
  if (
    typeof document === "undefined" ||
    document.visibilityState !== "hidden"
  ) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const cleanup = () => {
      document.removeEventListener("visibilitychange", onVis);
      signal?.removeEventListener("abort", onAbort);
    };
    const onVis = () => {
      if (document.visibilityState !== "hidden") {
        cleanup();
        resolve();
      }
    };
    const onAbort = () => {
      cleanup();
      resolve();
    };
    document.addEventListener("visibilitychange", onVis);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Window key the hero <video> reads to find an in-memory object URL of the
 * fully-downloaded clip. When present the player uses it INSTEAD of streaming
 * the CDN sphere montage, so playback never depends on the browser reusing this
 * fetch's HTTP cache for byte-range requests. Set once and kept alive for the
 * document session (never revoked — a single ~32 MB Blob).
 */
export const HERO_VIDEO_BLOB_URL_KEY = "__refgdHeroVideoBlobUrl";

/**
 * Build a Blob from the captured chunks and publish its object URL on window.
 * Best-effort: on OOM / unsupported it leaves the key unset and the player
 * silently falls back to streaming the URL. No-op if already published.
 */
function publishHeroBlobUrl(chunks: Uint8Array[]): void {
  try {
    const w = window as unknown as Record<string, unknown>;
    if (typeof w[HERO_VIDEO_BLOB_URL_KEY] === "string") return;
    const blob = new Blob(chunks as BlobPart[], { type: "video/mp4" });
    w[HERO_VIDEO_BLOB_URL_KEY] = URL.createObjectURL(blob);
  } catch {
    /* leave unset → player falls back to the URL <source> */
  }
}

/**
 * Fully download one asset, reporting received bytes via onProgress.
 * Resolves (never rejects) so a flaky asset can't strand the overlay.
 * Reading the body to completion also warms the HTTP cache, so the
 * component that later renders the scene reuses it instead of
 * re-downloading.
 */
export async function downloadAsset(
  asset: HeavyAsset,
  onProgress?: (receivedBytes: number, totalBytes: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  // Repeat-visit fast path: if the scene is already persisted in Cache Storage
  // (seeded on a previous visit), there is nothing to download — report
  // complete instantly so the splash never re-pulls the ~23 MB file.
  if (
    isSceneAsset(asset.url) &&
    /^https?:/.test(asset.url) &&
    typeof caches !== "undefined"
  ) {
    try {
      const cache = await caches.open(SCENE_CACHE);
      const hit = await cache.match(asset.url, { ignoreVary: true });
      if (hit) {
        onProgress?.(asset.bytesHint ?? 1, asset.bytesHint ?? 1);
        return;
      }
    } catch {
      /* Cache Storage unavailable — fall through to a normal network download */
    }
  }
  // Repeat-visit fast path for the captured hero video: rebuild the in-memory
  // object URL straight from the durable media cache so a refresh NEVER
  // re-streams the ~32 MB clip (its byte-range requests don't reuse the HTTP
  // cache on desktop/iOS, and the per-session Blob dies on reload).
  if (await serveCapturedFromCache(asset)) {
    onProgress?.(asset.bytesHint ?? 1, asset.bytesHint ?? 1);
    return;
  }
  // Resumable streaming download.
  //
  // A mobile tab that the user backgrounds (switches apps, opens another tab,
  // locks the phone) gets SUSPENDED mid-stream — iOS frequently kills the
  // in-flight fetch outright. The old code caught that error and reported the
  // asset "complete" with only a partial body cached, so the <video> then had
  // to re-buffer the rest from the network when the page was finally revealed.
  //
  // Instead we now RESUME: track how many bytes we have and, whenever the
  // stream is cut short, wait until the tab is foregrounded again and continue
  // from that byte with a `Range` request. The download therefore "keeps
  // going" across any number of background trips until the whole file lands in
  // the browser's (immutable) HTTP cache. Desktop tabs are never suspended, so
  // there the very first pass simply runs straight to the end.
  let received = 0;
  let total = asset.bytesHint ?? 0;
  let attempts = 0;
  // Captured-video metadata from the first full response, used to persist an
  // accurate, revalidate-able copy into the durable media cache on completion.
  let mediaType = "";
  let mediaEtag = "";
  // When asked, collect the streamed bytes so we can hand the player an
  // in-memory Blob (deterministic playback, no HTTP-cache-reuse dependency).
  // Set to null the moment we cannot guarantee contiguous bytes, which leaves
  // plain cache-warming as the fallback.
  const wantBlob = asset.captureBlob === true && typeof URL !== "undefined";
  let chunks: Uint8Array[] | null = wantBlob ? [] : null;
  for (;;) {
    if (signal?.aborted) {
      onProgress?.(total || 1, total || 1);
      return;
    }
    try {
      const headers: Record<string, string> = {};
      if (received > 0) headers.Range = `bytes=${received}-`;
      const res = await fetch(asset.url, {
        mode: "cors",
        credentials: "omit",
        signal,
        headers,
      });
      // Server ignored our Range and replied 200 from the start → reset the
      // counter so we don't append onto an offset that was never skipped.
      if (received > 0 && res.status === 200) {
        received = 0;
        if (chunks) chunks = []; // restart capture cleanly from byte 0
      }
      // Resumed 206: only keep capturing if the partial begins EXACTLY where we
      // left off; otherwise the Blob would be misordered/corrupt → abandon it
      // and let plain cache-warming take over.
      if (chunks && res.status === 206) {
        const m = /bytes\s+(\d+)-/i.exec(res.headers.get("content-range") || "");
        const start = m ? parseInt(m[1], 10) : NaN;
        if (!Number.isFinite(start) || start !== received) chunks = null;
      }
      if (!res.ok && res.status !== 206) {
        // Non-retryable HTTP status — warm whatever we can, report complete.
        try {
          await res.arrayBuffer();
        } catch {
          /* noop */
        }
        onProgress?.(total || 1, total || 1);
        return;
      }
      // On the first (full) response, seed the scene cache and lock in the
      // real byte total from the Content-Length header.
      if (received === 0) {
        if (
          isSceneAsset(asset.url) &&
          /^https?:/.test(asset.url) &&
          typeof caches !== "undefined"
        ) {
          void persistScene(asset.url, res.clone());
        }
        if (asset.captureBlob) {
          mediaType = res.headers.get("content-type") || mediaType;
          mediaEtag = res.headers.get("etag") || mediaEtag;
        }
        const lenHeader = res.headers.get("content-length");
        if (lenHeader) total = parseInt(lenHeader, 10) || total;
      }
      if (!res.body) {
        try {
          await res.arrayBuffer();
        } catch {
          /* noop */
        }
        onProgress?.(total || 1, total || 1);
        return;
      }
      const reader = res.body.getReader();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value && chunks) chunks.push(value);
        received += value?.length ?? 0;
        onProgress?.(received, total || received);
      }
      // Stream finished. If a known total says we are still short, the
      // connection was cut early (a background suspension) — resume once the
      // tab is visible again instead of giving up with a partial file.
      if (total && received < total && !signal?.aborted) {
        await waitUntilVisible(signal);
        if (signal?.aborted || ++attempts > 60) {
          onProgress?.(received, total);
          return;
        }
        continue;
      }
      // Whole file in hand → publish the in-memory object URL for the player
      // AND persist the exact bytes into the durable media cache so the next
      // load (including a hard refresh) rebuilds the clip from disk instead of
      // re-streaming it from the network.
      if (chunks && total > 0 && received === total) {
        publishHeroBlobUrl(chunks);
        // Await the durable write so an IMMEDIATE post-load hard refresh still
        // finds the clip in Cache Storage. Safe to await: persistMedia swallows
        // its own quota/unavailable errors, so it can't hang or throw. Only the
        // first-ever visit pays this short local write; repeats hit the fast path.
        if (asset.captureBlob) {
          await persistMedia(asset.url, chunks, mediaType, mediaEtag, total);
        }
      }
      onProgress?.(received, total || received);
      return;
    } catch {
      // The gate aborted us (component unmounted) → stop cleanly.
      if (signal?.aborted) {
        onProgress?.(total || received || 1, total || received || 1);
        return;
      }
      // Network error, or a suspension that killed the fetch. Wait until the
      // tab is foregrounded, then loop to resume from `received` via Range.
      await waitUntilVisible(signal);
      if (signal?.aborted || ++attempts > 60) {
        onProgress?.(total || received || 1, total || received || 1);
        return;
      }
      await new Promise((r) => setTimeout(r, 400));
    }
  }
}

/**
 * Persist a freshly fetched scene response into Cache Storage so it survives
 * across visits. Takes a CLONE of the in-flight response (the original keeps
 * streaming for progress), so this adds no extra network download. Best-effort:
 * swallows quota / write errors. No-op if the scene is already cached.
 */
async function persistScene(url: string, res: Response): Promise<void> {
  try {
    if (!res.ok) return;
    const cache = await caches.open(SCENE_CACHE);
    const existing = await cache.match(url, { ignoreVary: true });
    if (!existing) await cache.put(url, res);
  } catch {
    /* quota exceeded / Cache Storage unavailable — fine, the SW still caches it */
  }
}

/**
 * Repeat-visit fast path for a captured hero clip: if its exact bytes already
 * live in the durable media cache, rebuild the in-memory object URL straight
 * from them (zero network) and report success. Also kicks off a cheap
 * background ETag check so a re-published clip self-heals on the NEXT load.
 */
async function serveCapturedFromCache(asset: HeavyAsset): Promise<boolean> {
  if (!asset.captureBlob) return false;
  if (typeof caches === "undefined" || typeof URL === "undefined") return false;
  try {
    const cache = await caches.open(MEDIA_CACHE);
    const hit = await cache.match(asset.url, { ignoreVary: true });
    if (!hit || !hit.ok) return false;
    const lenHdr = parseInt(hit.headers.get("content-length") || "0", 10);
    const buf = await hit.arrayBuffer();
    // Only trust a byte-complete copy. We only ever store full files, but guard
    // against a truncated/evicted entry so we never publish a broken clip.
    if (!buf.byteLength || (lenHdr && buf.byteLength !== lenHdr)) {
      await cache.delete(asset.url);
      return false;
    }
    publishHeroBlobUrl([new Uint8Array(buf)]);
    void revalidateMedia(asset.url, hit);
    return true;
  } catch {
    return false;
  }
}

/**
 * Persist the freshly-downloaded hero clip into the durable media cache so the
 * next load rebuilds it from disk instead of re-streaming. Stores a Response
 * carrying the original content-type/etag/length so `revalidateMedia` can later
 * detect a re-published clip. Best-effort; swallows quota errors.
 */
async function persistMedia(
  url: string,
  chunks: Uint8Array[],
  type: string,
  etag: string,
  total: number,
): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const cache = await caches.open(MEDIA_CACHE);
    const headers: Record<string, string> = {
      "content-type": type || "video/mp4",
      "content-length": String(total),
    };
    if (etag) headers.etag = etag;
    const body = new Blob(chunks as BlobPart[], { type: type || "video/mp4" });
    await cache.put(url, new Response(body, { headers }));
  } catch {
    /* quota exceeded / unavailable — fall back to the HTTP cache */
  }
}

/**
 * Cheap freshness check for a cached hero clip — never re-downloads the body.
 * HEAD the URL and compare ETags; if the clip was re-published at the same URL,
 * drop the cached copy so the NEXT load fetches the new one exactly once. Runs
 * at most once per URL per page load.
 */
async function revalidateMedia(url: string, cached: Response): Promise<void> {
  if (mediaRevalidated.has(url)) return;
  mediaRevalidated.add(url);
  try {
    const head = await fetch(url, {
      method: "HEAD",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
    });
    if (!head || !head.ok) return;
    const fresh = head.headers.get("etag");
    const have = cached.headers.get("etag");
    if (fresh && have && fresh !== have && typeof caches !== "undefined") {
      const cache = await caches.open(MEDIA_CACHE);
      await cache.delete(url);
    }
  } catch {
    /* offline / HEAD blocked — keep the cached copy */
  }
}

/**
 * Download every heavy asset for a route, reporting an aggregate 0..1
 * fraction. Resolves when all settle. Never rejects.
 */
export async function downloadHeavyAssets(
  assets: HeavyAsset[],
  onFraction?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  if (assets.length === 0) {
    onFraction?.(1);
    return;
  }
  const received = new Array(assets.length).fill(0) as number[];
  const totals = assets.map((a) => a.bytesHint ?? 1);
  const emit = () => {
    const sumReceived = received.reduce((s, n) => s + n, 0);
    const sumTotal = totals.reduce((s, n) => s + n, 0) || 1;
    onFraction?.(Math.min(1, sumReceived / sumTotal));
  };
  await Promise.all(
    assets.map((a, i) =>
      downloadAsset(
        a,
        (r, t) => {
          received[i] = r;
          if (t) totals[i] = t;
          emit();
        },
        signal,
      ),
    ),
  );
  onFraction?.(1);
}

/**
 * All large hero media across the site, tagged with the route(s) that own
 * them. Used by the BackgroundPrefetcher to warm every OTHER route's heavy
 * video into the browser's (immutable, year-long) HTTP cache while the user
 * sits on the current page — so navigating to that page later plays straight
 * from cache with no buffering and no loading-screen wait.
 */
export type PrefetchAsset = HeavyAsset & { routes: string[] };

export const PREFETCHABLE_HEAVY_MEDIA: PrefetchAsset[] = [
  // Home "sphere montage" hero loop (~32 MB). It IS splash-gated on the home
  // page (see heavyAssetsForPath), so this entry only warms it when the user
  // is browsing OTHER pages — prefetchOtherRouteMedia skips the current route.
  {
    url: MEDIA_ASSETS.sphereMontage.url,
    bytesHint: MEDIA_ASSETS.sphereMontage.bytes,
    routes: ["/"],
  },
  // Evade-Cancelations neon-vortex hero.
  {
    url: MEDIA_ASSETS.evadeHeroVortex.url,
    bytesHint: MEDIA_ASSETS.evadeHeroVortex.bytes,
    routes: ["/evade-cancelations"],
  },
  // Exclusive-Mentorships "Liquid Reflections" hero loop.
  {
    url: MEDIA_ASSETS.mentorshipBackground.url,
    bytesHint: MEDIA_ASSETS.mentorshipBackground.bytes,
    routes: ["/exclusive-mentorships"],
  },
];

const prefetchedMedia = new Set<string>();
const prefetchInFlight = new Set<string>();

/**
 * Only background-prefetch when the connection can spare it. Respects the
 * user's Data Saver toggle and skips 2g-class links entirely so we never burn
 * a metered/slow mobile plan warming videos the user may never visit. Returns
 * true when the Network Information API is unavailable (can't tell → allow).
 */
function connectionAllowsPrefetch(): boolean {
  if (typeof navigator === "undefined") return true;
  const c = (
    navigator as unknown as {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  if (!c) return true;
  if (c.saveData) return false;
  if (c.effectiveType === "slow-2g" || c.effectiveType === "2g") return false;
  return true;
}

/** Resolve on the next idle slot (or after a short delay if unsupported). */
function whenIdle(): Promise<void> {
  return new Promise<void>((resolve) => {
    const ric = (
      window as unknown as {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number },
        ) => number;
      }
    ).requestIdleCallback;
    if (typeof ric === "function") ric(() => resolve(), { timeout: 3000 });
    else window.setTimeout(resolve, 600);
  });
}

/**
 * Warm every heavy video that does NOT belong to the current route into the
 * HTTP cache, one at a time, during idle slots. Fire-and-forget: it is NOT
 * tied to the page lifecycle (no abort on navigation) so a large file finishes
 * even if the user moves around, and the resumable `downloadAsset` keeps it
 * going across mobile tab suspensions. Module-level sets dedupe across route
 * changes so each file is fetched at most once per session (and repeat visits
 * are served from the immutable cache with no network at all).
 */
export async function prefetchOtherRouteMedia(
  currentPath: string,
): Promise<void> {
  if (typeof window === "undefined") return;
  if (!connectionAllowsPrefetch()) return;
  const here = norm(currentPath);
  for (const asset of PREFETCHABLE_HEAVY_MEDIA) {
    if (asset.routes.some((r) => norm(r) === here)) continue;
    if (prefetchedMedia.has(asset.url) || prefetchInFlight.has(asset.url)) {
      continue;
    }
    await whenIdle();
    // Re-check after the idle wait: the user may have toggled Data Saver, the
    // connection may have degraded, or another mount may have claimed this file.
    if (!connectionAllowsPrefetch()) return;
    if (prefetchedMedia.has(asset.url) || prefetchInFlight.has(asset.url)) {
      continue;
    }
    prefetchInFlight.add(asset.url);
    try {
      await downloadAsset({ url: asset.url, bytesHint: asset.bytesHint });
      prefetchedMedia.add(asset.url);
    } finally {
      prefetchInFlight.delete(asset.url);
    }
  }
}
